import { Component, OnInit, Inject, LOCALE_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { lmwBaseRoutes } from '../../../routing/route-locales/lmw-base-routes';
import { MyLearningHome } from '../../../interfaces/my-learning-home.interface';
import { TopicService } from '../../../../topic-player/services/topic.service';
import { environment } from 'apps/learn-my-way/src/environments/environment';
import { TranslateService } from '@nx/mono-common';
import { MyLearningCms } from '../../../interfaces/my-learning-cms.interface';

@Component({
  selector: 'lmw-base-my-learning-home',
  templateUrl: './my-learning-home.component.html',
})
export class MyLearningHomeComponent implements OnInit {
  public subjects: any;
  public userFullName: any;
  public browseAllSubjectsBasePath: string = lmwBaseRoutes.browseSubjectsStatePath;
  public allSubjectsAndTopicInfo: MyLearningHome[] = [];
  public showSubjectsList = false;
  public language: string = environment.language;
  public myLearningCms: MyLearningCms;
  public mobileBackButtonText = lmwBaseRoutes.homeLinkText;

  private latestTopicAttempts = [];

  constructor(
    private route: ActivatedRoute,
    private topicService: TopicService,
    private translateService: TranslateService,
    @Inject(LOCALE_ID) private localeId: string
  ) {}

  ngOnInit(): void {
    this.myLearningCms = this.route.snapshot.data.cmsData;
    this.subjects = this.route.snapshot.data.subjects;
    this.userFullName = this.route.snapshot.data.userClaims.user.apps.lmw.fullName;
    this.getAllTopicAttempts();
  }

  public translate(template: string) {
    return this.translateService.translate(template, this.language);
  }

  public printSummary() {
    window.print();
  }

  private getAllTopicAttempts() {
    this.topicService
      .getTopicsAttemptsByCompleted(true)
      .subscribe((topicsFound) => {
        if (topicsFound.length > 0)
        this.latestTopicAttempts = this.removeDuplicateTopicSlugsByLatestDateUpdated(
          topicsFound
        );
        this.populateSubjectsAndTopicsObject();
      });
  }

  private removeDuplicateTopicSlugsByLatestDateUpdated(topicsCompleted) {
    return Object.values(
      topicsCompleted.reduce(
        (reducedTopicsCompletedArray, { SK, dateUpdated }) => {
          const topicSlugFromSK = SK.split(':')[1];
          if (reducedTopicsCompletedArray[topicSlugFromSK]) {
            if (
              reducedTopicsCompletedArray[topicSlugFromSK].dateUpdated <
              dateUpdated
            ) {
              reducedTopicsCompletedArray[topicSlugFromSK] = {
                topicSlugFromSK,
                dateUpdated,
              };
            }
          } else
            reducedTopicsCompletedArray[topicSlugFromSK] = {
              topicSlugFromSK,
              dateUpdated,
            };
          {
            return reducedTopicsCompletedArray;
          }
        },
        {}
      )
    );
  }

  private populateSubjectsAndTopicsObject() {
    this.subjects.forEach((subject, index) => {
      this.allSubjectsAndTopicInfo.push({
        subjectSlug: subject.sk,
        totalTopicsInSubject: 0,
        topicsCompletedInSubject: 0,
        latestTopicUpdatedDate: '',
        subjectCompleted: false,
      });
      this.getTotalTopicsInSubject(subject.sk, index);
    });
  }

  private getTotalTopicsInSubject(subjectSlug, index) {
    this.topicService
      .getTopicsBySubjectAndLocale(subjectSlug, this.localeId)
      .subscribe((topicsInSubject) => {
        this.allSubjectsAndTopicInfo[index].totalTopicsInSubject =
          topicsInSubject.length;
          if (this.latestTopicAttempts.length > 0) {
            this.traverseLatestTopicAttemptsToCheckForSubject(subjectSlug, index);
          } else {
            this.checkIfAllSubjectInfoHasBeenPopulated();
          }
      });
  }

  private traverseLatestTopicAttemptsToCheckForSubject(subjectSlug, index) {
    this.latestTopicAttempts.forEach((topic) => {
      this.getSubjectCompletedTopicsBelongTo(
        subjectSlug,
        topic.topicSlugFromSK,
        index
      );
    });
  }

  private getSubjectCompletedTopicsBelongTo(
    subjectSlug,
    topicAttemptSlug,
    index
  ) {
    this.topicService
      .doesTopicBelongToSubject(subjectSlug, topicAttemptSlug, this.localeId)
      .subscribe((completedTopicFoundInSubject) => {
        if (completedTopicFoundInSubject) {
          const topicCompletedDate = this.filterDateFromTopicAttempt(
            topicAttemptSlug
          );
          this.allSubjectsAndTopicInfo[index].topicsCompletedInSubject += 1;
          if (
            this.allSubjectsAndTopicInfo[index].latestTopicUpdatedDate <
            topicCompletedDate
          ) {
            this.allSubjectsAndTopicInfo[
              index
            ].latestTopicUpdatedDate = this.formatDateForLocale(
              topicCompletedDate
            );
          }
        }
        this.checkIfAllSubjectInfoHasBeenPopulated();
      });
  }

  private checkIfAllSubjectInfoHasBeenPopulated() {
    if (this.allSubjectsAndTopicInfo.length === this.subjects.length) {
      this.checkForCompleteSubjects();
    }
  }

  private checkForCompleteSubjects() {
    for (let i = 0; i < this.allSubjectsAndTopicInfo.length; i++) {
      if (
        this.allSubjectsAndTopicInfo[i].topicsCompletedInSubject ===
        this.allSubjectsAndTopicInfo[i].totalTopicsInSubject
      ) {
        this.allSubjectsAndTopicInfo[i].subjectCompleted = true;
      } else {
        this.allSubjectsAndTopicInfo[i].subjectCompleted = false;
      }
    }
    this.showSubjectsList = true;
  }

  private filterDateFromTopicAttempt(topicAttemptSlug) {
    return this.latestTopicAttempts
      .filter((item) => item.topicSlugFromSK === topicAttemptSlug)
      .map((item) => item.dateUpdated)
      .toString();
  }

  private formatDateForLocale(topicCompletedDate): string {
    return new Date(topicCompletedDate)
      .toLocaleString(this.localeId)
      .split(',')[0];
  }
}
